import { ChatCreatedResponseInterface } from '../buddy-data-structures/chat-created-response.interface';
import { firstValueFrom, map, Observable, of, scan } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BUDDY_AUTH_SERVICE_TOKEN } from './buddy-auth-service-token';
import { AuthService } from 'src/app/auth/auth.service';
import { BuddyConfigService } from './buddy-config.service';
import { BuddyMessage } from '../buddy-data-structures/buddy-message';
import { environment } from '../../../../environments/environment';
import { ChatbotReply } from './chatbot.reply';

@Injectable()
export class BuddyApiService {
    constructor(
        private readonly http: HttpClient,
        @Inject(BUDDY_AUTH_SERVICE_TOKEN) private readonly authService: AuthService,
        private readonly buddyConfigService: BuddyConfigService,
    ) {}

    /**
     * Creates a new chat.
     * @returns a promise that resolves to the chat id of the newly created chat.
     */
    async createChat(): Promise<ChatCreatedResponseInterface> {
        const useAuth = this.buddyConfigService.buddyConfig.useAuth;
        const endpoint = this.buddyConfigService.buddyConfig.endpoint;

        if (useAuth) {
            const accessToken = (await firstValueFrom(this.authService.session$)).tokenSet.access_token;
            return firstValueFrom(
                this.http.post<ChatCreatedResponseInterface>(
                    new URL(`connected/chats`, endpoint).toString(),
                    {},
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                        withCredentials: true,
                    },
                ),
            );
        } else {
            return firstValueFrom(this.http.post<ChatCreatedResponseInterface>(`${endpoint}/chats`, {}));
        }
    }

    /**
     * Sends a message payload to the AI service for a given chat ID.
     * Utilizes authentication if configured.
     *
     * @param {string} chatId - The unique identifier for the chat session.
     * @param {string} messagePayload - The message content to be sent.
     * @returns {Promise<Response>} - A promise resolving to the AI service response.
     * @throws Will throw an error if the request fails.
     */
    async sendToAI(chatId: string, messagePayload: string): Promise<Response> {
        console.group(`sendToAI() started for chatId: ${chatId}`);

        try {
            // Retrieve configuration values
            const { useAuth, endpoint, iconUrl, name } = this.buddyConfigService.buddyConfig;
            let url = new URL(`chats/${chatId}/messages`, endpoint).toString();
            if (this.buddyConfigService.buddyConfig.useAuth) {
                url = new URL(`connected/chats/${chatId}/messages`, endpoint).toString();
            }

            // Set up request headers
            let headers: Record<string, string> = {
                'Content-Type': 'application/json',
            };

            if (useAuth) {
                const accessToken = (await firstValueFrom(this.authService.session$)).tokenSet.access_token;
                headers.Authorization = accessToken;
            }

            // Send POST request
            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: messagePayload,
            });

            if (!response.ok) {
                console.error(`Response Error [${response.status}]: ${response.statusText}`);
            }

            return response;
        } catch (error) {
            console.error('Error occurred during sendToAI execution:', error);
            throw error;
        } finally {
            console.groupEnd();
        }
    }

    private readableStreamToObservable(readableStream: ReadableStream<string>): Observable<string> {
        const reader = readableStream.getReader();
        return new Observable<string>((subscriber) => {
            let buffer = ''; // Buffer to store incomplete lines

            function read() {
                reader
                    .read()
                    .then(({ done, value }) => {
                        if (done) {
                            // Send any remaining data in the buffer before completing
                            if (buffer.length > 0) {
                                subscriber.next(buffer);
                            }
                            subscriber.complete();
                        } else {
                            // Append new chunk to existing buffer
                            buffer += value;

                            // Split by newlines, but keep the last part in the buffer if it doesn't end with newline
                            const lines = buffer.split('\n');

                            // If the buffer ends with a newline, all lines are complete
                            // Otherwise, keep the last line in the buffer as it might be incomplete
                            const isLastLineComplete = buffer.endsWith('\n');

                            // Process all complete lines
                            const completeLines = isLastLineComplete ? lines : lines.slice(0, -1);
                            completeLines.forEach((line) => {
                                if (line.trim()) {
                                    // Only emit non-empty lines
                                    subscriber.next(line);
                                }
                            });

                            // Update buffer with the incomplete line if any
                            buffer = isLastLineComplete ? '' : lines[lines.length - 1];

                            read();
                        }
                    })
                    .catch((error) => {
                        subscriber.error(error);
                    });
            }

            read();
            return () => reader.cancel();
        });
    }

    responseAsObservable(response: Response): Observable<ChatbotReply> {
        if (!response.ok) {
            const error = {
                statusCode: response.status,
                statusText: response.statusText,
                body: response.body,
                text: response.text,
            };
            console.error(error);
            return of({ error });
        }
        const textStream = response.body.pipeThrough(new TextDecoderStream());
        return this.readableStreamToObservable(textStream).pipe(
            map((chunk) => this.processChunk(chunk)),
            scan(
                (acc, curr) => ({
                    ...acc,
                    ...curr,
                    content: acc.content + curr?.content ?? '',
                    stop: acc.stop || curr?.stop, // this overrides the stop property with true if it was once true before
                }),
                {
                    content: '',
                    message_id: '',
                    stop: false,
                },
            ),
        );
    }

    private processChunk(chunk: string): ChatbotReply {
        if (chunk.startsWith('data: ')) {
            try {
                return JSON.parse(chunk.substring(6));
            } catch (error) {
                console.error(`Error parsing JSON for chunk '${chunk}':`, error);
            }
        }
    }

    async fetchChatHistory(chatId: string): Promise<BuddyMessage[]> {
        const accessToken = (await firstValueFrom(this.authService.session$)).tokenSet.access_token;
        return firstValueFrom(
            this.http.get<BuddyMessage[]>(`${environment.CHATBOT_BACKEND}/connected/chats/${chatId}/messages`, {
                headers: {
                    Authorization: accessToken,
                },
                withCredentials: true,
            }),
        );
    }
}
